import { request, requestBlob } from "./http";
import { buildListQuery } from "./query";

const collection = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.records)) return response.records;
  return [];
};

export const listarDocumentos = (params = {}) => {
  const { perfil = "ventas_listado_grid_new", ...list } = params;
  return request(
    `encabezados?perfil=${encodeURIComponent(perfil)}&${buildListQuery(list)}`
  );
};

export const obtenerDocumento = (id, perfil = "docto_vistaimpresion") =>
  request(
    `encabezados?perfil=${perfil}&${buildListQuery({
      limit: 1,
      filters: [{ property: "id", operator: "=", value: id }],
    })}`
  ).then((response) => collection(response)[0] || response?.data || response);

export const listarDocumentosPrevios = ({
  idDocumentoPosterior,
  idEmpresa,
  search = "",
  page = 1,
  limit = 25,
}) => {
  const filters = [
    {
      relation: "documento.posteriores",
      property: "id_documento_relacionado",
      value: Number(idDocumentoPosterior),
      operator: "=",
    },
    {
      relation: "detalles",
      property: "saldo",
      value: 0,
      operator: ">",
    },
    {
      property: "estatus",
      value: "B",
      operator: "!=",
    },
  ];

  filters.push({ property: "estatus", operator: "!=", value: "C" });
  if (idEmpresa) {
    filters.push({
      property: "id_empresa",
      value: Number(idEmpresa),
      operator: "=",
    });
  }

  if (search.trim()) {
    filters.unshift({
      group: [
        {
          property: "folio",
          value: search.trim(),
          operator: "LIKE",
          condition: "OR",
        },
        {
          property: "nombre_solicita",
          value: search.trim(),
          operator: "LIKE",
          condition: "OR",
        },
        {
          relation: "cliente",
          property: "nombre",
          value: search.trim(),
          operator: "LIKE",
          condition: "OR",
        },
        {
          property: "referencia",
          value: search.trim(),
          operator: "LIKE",
          condition: "OR",
        },
      ],
      condition: "AND",
    });
  }

  return listarDocumentos({
    perfil: "previos_posteriores_view",
    page,
    limit,
    filters,
    sort: [{ property: "created_at", direction: "DESC" }],
  });
};

export const obtenerDocumentoPrevio = async (id) => {
  const [header, details] = await Promise.all([
    obtenerDocumento(id),
    obtenerDetallesDocumento(id),
  ]);
  return { ...header, detalles: details.data };
};
export const obtenerDetallesDocumento = async (id) => {
  const all = [];
  let page = 1,
    last = 1;
  do {
    const result = await request(
      `get-detalles-documento?${buildListQuery({
        page,
        limit: 100,
        filters: [
          { property: "id_encabezado", operator: "=", value: Number(id) },
        ],
      })}`
    );
    const rows = collection(result);
    all.push(...rows);
    last = Number(result.last_page || 1);
    page++;
  } while (page <= last);
  return { data: all, total: all.length, last_page: 1 };
};

export const listarConfiguracionesDocumento = (params) =>
  request(`documentos?${buildListQuery(params)}`);

export const listarCatalogoClientes = (params) =>
  request(`clientes?lookup=1&${buildListQuery(params)}`);

export const obtenerClienteDocumento = (id) =>
  request(`clientes/${id}?lookup=1&perfil=all`);

export const listarEmpresas = (params) =>
  request(`empresas?${buildListQuery(params)}`);

export const obtenerEmpresa = (id) => request(`empresas/${id}?perfil=all`);

export const listarDirecciones = (params) =>
  request(`direcciones?${buildListQuery(params)}`);

export const obtenerDireccion = (id) =>
  listarDirecciones({
    page: 1,
    limit: 1,
    filters: [{ property: "id", operator: "=", value: Number(id) }],
  }).then((response) => collection(response)[0] || response?.data || response);

export const obtenerTipoDescuento = () => request("get-tipo-descuento");

export const obtenerSaldoCuentasCobrar = (idCliente) =>
  request(`cuentas-cobrar-saldo?id_cliente=${encodeURIComponent(idCliente)}`);

export const obtenerSaldoVencidoCuentasCobrar = (idCliente) =>
  request(
    `cuentas-cobrar-vencidas?id_cliente=${encodeURIComponent(idCliente)}`
  );

export const listarCatalogoProductos = (params) =>
  request(
    `productos?lookup=1&perfil=detalles_edicion&${buildListQuery(params)}`
  );

export const obtenerProductoDocumento = (id) =>
  request(`productos/${id}?lookup=1&perfil=detalles_edicion`);

export const listarPreciosEspeciales = (params) =>
  request(`precios-especiales?${buildListQuery(params)}`);

export const obtenerPrecioEspecial = (id) =>
  request(`precios-especiales/${id}`);

export const listarAlmacenes = (params) =>
  request(`almacenes?${buildListQuery(params)}`);

export const obtenerAlmacen = (id) => request(`almacenes/${id}`);

export const listarVehiculos = (params) =>
  request(`vehiculos?${buildListQuery(params)}`);

export const obtenerVehiculo = (id) => request(`vehiculos/${id}`);

const listarSat = (catalogo, params = {}) =>
  request(`facturacion/catalogos/sat/${catalogo}?${buildListQuery(params)}`);

export const listarFormasPagoSat = (params) => listarSat("formas_pago", params);
export const listarMetodosPagoSat = (params) =>
  listarSat("metodos_pago", params);
export const listarUsosComprobanteSat = (params) =>
  listarSat("usos_comprobantes", params);

const sendPayload = (path, payload, override) => {
  const body = new FormData();
  body.append("payload", JSON.stringify(payload));
  return request(path, {
    method: "POST",
    body,
    headers: override ? { "X-HTTP-Method-Override": override } : undefined,
    timeout: 120000,
  });
};

export const crearDocumento = (payload) =>
  sendPayload("nuevo-documento", payload);
export const actualizarDocumento = (id, payload) =>
  sendPayload(`editar-documento/${id}`, payload, "PATCH");

export const cancelarDocumento = (id, forzar = false) => {
  const body = new URLSearchParams({ id_documento: String(id) });
  if (forzar) body.set("forzar_cancelar", "true");
  return request("cancelar-documento", {
    method: "POST",
    body,
    headers: {
      "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
    },
  });
};

export const obtenerPdfDocumento = (id) =>
  requestBlob(`documentos-venta/${Number(id)}/pdf`);

export const listarMonedasDocumento = () => request("monedas?limit=1000");
export const listarConceptosPresupuesto = (params) => request(`conceptos-presupuesto?${buildListQuery(params)}`);
export const obtenerConceptoPresupuesto = (id) => request(`conceptos-presupuesto/${Number(id)}`);
